<template>
  <v-card v-bind="$attrs" class="bg-background" :class="{ scrollable }" elevation="0">
    <v-card-title v-if="!flat" class="bg-light-grey px-2 mw-0">
      <div class="d-flex flex-grow-1 align-center justify-space-between text-mid-dark-grey mw-0">
        <div class="d-flex align-center mw-0">
          <qtm-icon-btn
            color="mid-grey"
            icon="mdi-download"
            :href="src"
            target="_blank"
            title="Download"
          />
          <div class="ml-2 mw-0">
            <div class="qtm-h4 text-truncate" v-text="name" />
            <div v-if="isPDF" class="qtm-overline">
              {{ numberOfPages }} Page{{ numberOfPages > 1 ? 's' : '' }}
            </div>
          </div>
        </div>
        <qtm-icon-btn
          color="mid-grey"
          icon="mdi-close"
          title="Close document viewer"
          @click="$emit('close')"
        />
      </div>
    </v-card-title>
    <v-card-text ref="el" :class="{ 'overflow-y-auto': scrollable, [`pa-${padding}`]: true }">
      <div v-if="isPDF && initialWidthSet">
        <vue-pdf-embed
          disable-annotation-layer
          :text-layer="selectTextLayer"
          :source="src"
          :width="componentWidth"
          @loaded="pdfLoaded"
          @rendered="onRender"
        >
          <template v-slot:after-page>
            <div class="mb-2" />
          </template>
        </vue-pdf-embed>
      </div>
      <div v-else-if="isImage">
        <v-img
          max-width="100%"
          :alt="name"
          :src="src"
        />
      </div>
      <div v-else class="mt-16 pt-16 qtm-h1 text-center">
        <div>
          Cannot view file
        </div>
        <v-btn
          :loading="downloading"
          title="Download"
          size="x-large"
          variant="text"
          @click="download(name)"
        >
          <v-icon color="interactive" location="left">
            mdi-download
          </v-icon>
          {{ name }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import VuePdfEmbed from 'vue-pdf-embed'
import useAttachments from '@/composables/attachments'

export interface Props {
  flat?: boolean
  name: string
  padding?: number | string
  scrollable?: boolean
  selectTextLayer?: boolean
  src: string
}

const props = withDefaults(defineProps<Props>(), {
  padding: 0,
})

defineEmits(['close'])

const el = useTemplateRef('el')
const componentWidth = ref(500)
const pendingWidth = ref()
const renderComplete = ref(false)
const initialWidthSet = ref(false)

const improveCopiedText = (textToImprove: string) => {
  const text = textToImprove.trim()
  if (!/[a-zA-Z]/.test(text)) {
    return text.replace(/[^\d./-]/g, '')
  }
  return text
}

const { $toast } = useNuxtApp()

const onRender = () => {
  if (pendingWidth.value) {
    componentWidth.value = pendingWidth.value
    pendingWidth.value = null
    return
  }

  renderComplete.value = true

  if (props.selectTextLayer) {
    Array.from(document.getElementsByClassName('textLayer')).forEach(elem => {
      Array.from(elem.children).forEach(span => {
        span.addEventListener('mouseup', (event) => {
          let selectedText = ''

          if (window.getSelection) {
            selectedText = window.getSelection()?.toString() ?? ''
          }

          let text = selectedText || (event.target as any)?.innerHTML

          text = improveCopiedText(text)
          navigator.clipboard.writeText(text)
          $toast.toast(`📋 ${text}`, { color: 'darkgrey' })
        }, false)
      })
    })
  }
}

useResizeObserver(el, (entries) => {
  const entry = entries[0]
  let { width } = entry.contentRect

  width = Math.trunc(width)

  if (width && width !== componentWidth.value) {
    if (!renderComplete.value) {
      pendingWidth.value = width
    }
    else {
      renderComplete.value = false
      componentWidth.value = width
    }
  }
})

const { isImage, isPDF, downloading, download } = useAttachments(toRef(props, 'src'))

const numberOfPages = ref(1)

const pdfLoaded = (pdfProxy: any) => {
  numberOfPages.value = pdfProxy.numPages
}

onMounted(() => {
  if (el.value) {
    const width = el.value.$el.clientWidth

    if (width) {
      componentWidth.value = width
    }
  }

  initialWidthSet.value = true
})
</script>

<style lang="scss">
.textLayer {
  margin: auto;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0.2;
  line-height: 1;

  ::selection {
    background-color: rgb(var(--v-theme-primary)) !important;
  }
}

.textLayer > span {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
}

.textLayer > span:hover {
  background-color: yellow;
  cursor: pointer;
}
</style>

<style lang="scss" scoped>
// https://github.com/vuetifyjs/vuetify/blob/master/packages/vuetify/src/components/VDialog/VDialog.sass
.scrollable {
  display: flex;
  flex: 1 1 100%;
  flex-direction: column;
  max-height: 100%;
  max-width: 100%;
}

.mw-0 {
  min-width: 0;
}
</style>
